/**
 * Theme.js - Main theme JavaScript
 * 
 * Architecture:
 * - Uses EVENT DELEGATION for all click handlers (one listener, works with AJAX)
 * - Init functions run once on page load and after AJAX navigation
 * - No duplicate listeners, clean and predictable
 * - CartState provides optimistic UI updates with debounced server sync
 */

// Legacy animation (commented out for future use)
// import { changeBackground, BG_TRANSITION_DURATION, CONTENT_FADE_DURATION } from "./legacy/animation.js";
import "./cart-state.js";      // Must load first - provides optimistic cart state management
import "./product-action.js";
import "./side-cart.js";
import { initCookieBanner, initCookieSettingsTrigger } from "./site-consent.js";
import { initFreeShippingBanner, syncPolicyThresholdPlaceholders } from "./free-shipping-banner.js";
import { initHeaderMetrics } from "./header-metrics.js";
import { subscribeScrollTask } from "./scroll-priority.js";
import { initSectionSlides } from "./slides-section.js";
import { initProjectGallery } from "./project-gallery.js";
import { handleTabNavigation } from "./header.js";
import { initPoliciesNavigation } from "./page-policies.js";
import { loadPagination } from "./pagination.js";
import { loadCollectionContent, initCollectionFromHash, loadSearchContent, initSearchInput } from "./collection.js";

const CONTENT_FADE_DURATION = 500;

const SELECTORS = {
    logo: '[data-logo]',
    // Strict selector for top-level tabs that are NOT triggers or inside dropdowns
    headerTab: '[data-nav-tab]:not([data-dropdown-trigger]):not([data-dropdown-tab])',
    // headerTabAll: '[data-nav-tab]',
    headerTabAll: '[data-header-tab]',

    dropdownWrapper: '[data-dropdown-wrapper]',
    dropdownTrigger: '[data-dropdown-trigger]', // TODO: check if the assigned active still necessary
    dropdownMenu: '[data-dropdown-menu]',
    dropdownTab: '[data-dropdown-tab]',
    
    headerMobileMenu: '[data-header-mobile-menu]',
    headerMobileMenuTrigger: '[data-header-mobile-menu-trigger]',
    mobileDropdownWrapper: '[data-mobile-dropdown-wrapper]',
    mobileDropdownTrigger: '[data-mobile-dropdown-trigger]',
    mobileDropdownToggle: '[data-mobile-dropdown-toggle]',
    
    collectionMenuItem: '[data-collection-tab]',
    collectionContainer: '[data-collection-container]',
    collectionMenuH2: '[data-collection-tab]',
    collectionSearch: '.collections-menu__search',
    collectionPaginationLink: '.collection-content__pagination a',
    
    productItem: '[data-card-product]',
    projectCard: '[data-card-project]',

    headerContent: '.header-desktop__content',
    logoContainer: '.logo-container',
    slidesSection: '[data-slides-wrapper]',
    homepageBanner: '.homepage .banner-image',

    navigationElement: '[data-action="navigate"], [data-nav-element]',
    buttonElement: '[data-button-wrapper]',

    languageForm: '.header-icons__language-form',
};

(function () {
    'use strict';

    let homepageBannerMobileScrollState;

    // =========================================================================
    // EVENT DELEGATION - Single click handler for all interactive elements
    // =========================================================================

    document.addEventListener('click', function(e) {
        // ─────────────────────────────────────────────────────────────────────
        // Logo click - navigate to homepage
        // ─────────────────────────────────────────────────────────────────────
        const logo = e.target.closest(SELECTORS.logo);
        if (logo) {
            // Allow default behavior for modifier keys
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
            e.preventDefault();

            // Reset all tab states
            document.querySelectorAll(SELECTORS.headerTabAll).forEach(t => t.classList.remove('active'));
            document.querySelectorAll(SELECTORS.dropdownMenu).forEach(m => m.classList.remove('show'));
            document.querySelectorAll(SELECTORS.dropdownTab).forEach(i => i.classList.remove('active'));
            document.querySelectorAll(SELECTORS.dropdownWrapper).forEach(w => w.classList.remove('active'));
            document.querySelectorAll(SELECTORS.dropdownTrigger).forEach(t => t.classList.remove('active'));

            // Navigate to homepage
            const anchor = logo.closest('a') || logo;
            const url = anchor.href || '/';
            handleTabNavigation(url, e, CONTENT_FADE_DURATION);
            return;
        }

        // ─────────────────────────────────────────────────────────────────────
        // Header tab click - navigate to page
        // ─────────────────────────────────────────────────────────────────────
        const headerTab = e.target.closest(SELECTORS.headerTab);
        if (headerTab) {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
            
            const url = headerTab.dataset.href || headerTab.getAttribute('href');
            if (!url) return;

            e.preventDefault();

            // 1. Set THIS active
            document.querySelectorAll(SELECTORS.headerTabAll).forEach(t => {
                if (t === headerTab) {
                    t.classList.add('active');
                } else {
                    t.classList.remove('active');
                }
            });

            // 2. Clear all dropdown states (active items and active triggers)
            document.querySelectorAll(SELECTORS.dropdownTab).forEach(i => i.classList.remove('active'));
            document.querySelectorAll(SELECTORS.dropdownMenu).forEach(m => m.classList.remove('show')); // if used
            document.querySelectorAll(SELECTORS.dropdownWrapper).forEach(w => w.classList.remove('active'));
            document.querySelectorAll(SELECTORS.dropdownTrigger).forEach(t => t.classList.remove('active'));

            document.querySelectorAll(SELECTORS.mobileDropdownTrigger).forEach(t => t.classList.remove('active'));

            // 3. Close all mobile dropdowns
            document.querySelectorAll(SELECTORS.mobileDropdownToggle).forEach(mobileToggle => {
                mobileToggle.checked = false;
            });

            handleTabNavigation(url, e, CONTENT_FADE_DURATION);
            return;
        }

        // ─────────────────────────────────────────────────────────────────────
        // Dropdown item click - Check this BEFORE the wrapper to avoid overshadowing
        // ─────────────────────────────────────────────────────────────────────
        const dropdownTab = e.target.closest(SELECTORS.dropdownTab);
        if (dropdownTab) {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
            e.preventDefault();
            e.stopPropagation();

            const url = dropdownTab.dataset.href || dropdownTab.getAttribute('href');
            if (!url) return;

            // 1. Set THIS tab active
            document.querySelectorAll(SELECTORS.dropdownTab).forEach(t => {
                t.classList.toggle('active', t === dropdownTab);
            });

            // 2. Identify Parent Wrapper & Trigger
            const wrapper = dropdownTab.closest(SELECTORS.dropdownWrapper);
            const trigger = wrapper ? wrapper.querySelector(SELECTORS.dropdownTrigger) : null;

            // 3. Set Parent active (Wrapper and Trigger)
            document.querySelectorAll(SELECTORS.dropdownWrapper).forEach(w => {
                w.classList.toggle('active', w === wrapper);
            });

            if (trigger) {
                document.querySelectorAll(SELECTORS.dropdownTrigger).forEach(t => {
                    t.classList.toggle('active', t === trigger);
                });
            }

            // 4. Clear Top Level Tabs
            document.querySelectorAll(SELECTORS.headerTab).forEach(t => t.classList.remove('active'));

            handleTabNavigation(url, e, CONTENT_FADE_DURATION);
            return;
        }

        // ─────────────────────────────────────────────────────────────────────
        // Header main menu click - Handle null close
        // ─────────────────────────────────────────────────────────────────────
        // const headerMobileMenuClicking = e.target;
        // if (headerMobileMenuClicking) {
        //     console.log(headerMobileMenuClicking);
        // }

        // ─────────────────────────────────────────────────────────────────────
        // Header mobile dropdown wrapper click - Handle accordion toggles
        // ─────────────────────────────────────────────────────────────────────
        const headerMobileDropdownWrapper = e.target.closest(SELECTORS.mobileDropdownWrapper);
        if (headerMobileDropdownWrapper) {
            const checkboxId = headerMobileDropdownWrapper.getAttribute('for');

            // Close other open mobile dropdowns
            document.querySelectorAll(SELECTORS.mobileDropdownToggle).forEach(dropdownToggle => {
                const targetId = dropdownToggle.getAttribute('id');
                if (targetId !== checkboxId) {
                    dropdownToggle.checked = false;
                }
            });

            // Add active to current trigger and remove from others
            const trigger = headerMobileDropdownWrapper.matches(SELECTORS.mobileDropdownTrigger) 
                ? headerMobileDropdownWrapper 
                : headerMobileDropdownWrapper.querySelector(SELECTORS.mobileDropdownTrigger);
            trigger.classList.toggle('active');

            document.querySelectorAll(SELECTORS.mobileDropdownTrigger).forEach(t => {
                // t.classList.toggle('active', t === trigger);
                if (t !== trigger) {
                    t.classList.remove('active');
                }
            });

            // Remove active class from headerTabs
            document.querySelectorAll(SELECTORS.headerTabAll).forEach(t => t.classList.remove('active'));
            
            return;
        }

        // ─────────────────────────────────────────────────────────────────────
        // Collection menu item click - AJAX load collection (uses hash)
        // ─────────────────────────────────────────────────────────────────────
        const collectionMenuItem = e.target.closest(SELECTORS.collectionMenuItem);

        if (collectionMenuItem) {
            // Allow default behavior for mobile devices
            if (window.innerWidth <= 768) return;

            // Allow default behavior for modifier keys if it's a link
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
            
            const collectionUrl = collectionMenuItem.dataset.href || collectionMenuItem.getAttribute('href');
            const handle = collectionMenuItem.dataset.handle;

            if (!collectionUrl) return;
            
            e.preventDefault();

            // Update active states
            // TODO: check for toggle logic
            document.querySelectorAll(SELECTORS.collectionMenuItem).forEach(item => {
                item.classList.toggle('active', item === collectionMenuItem);
            });

            // Load collection content via AJAX (updates hash, not full URL)
            loadCollectionContent(handle, collectionUrl, true, CONTENT_FADE_DURATION);
            return;
        }

        // ─────────────────────────────────────────────────────────────────────
        // Collection pagination click
        // ─────────────────────────────────────────────────────────────────────
        const collectionPaginationLink = e.target.closest(SELECTORS.collectionPaginationLink);
        if (collectionPaginationLink) {
             if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
             e.preventDefault();
             const url = collectionPaginationLink.getAttribute('href');
             if (url) {
                 loadPagination(url, CONTENT_FADE_DURATION);
             }
             return;
        }

        
        // ─────────────────────────────────────────────────────────────────────
        // Navigate
        // ─────────────────────────────────────────────────────────────────────
        const navElem = e.target.closest(SELECTORS.navigationElement);
        const isButton = e.target.closest(SELECTORS.buttonElement);
        if (navElem && !isButton) {
            // Allow default behavior for modifier keys if it's a link
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;

            const url = navElem.dataset.href || navElem.getAttribute('href');

            if (!url) return;
            
            e.preventDefault();

            handleTabNavigation(url, e, CONTENT_FADE_DURATION);
            return;
        }
    });

    // =========================================================================
    // FORM SUBMISSION DELEGATION
    // =========================================================================
    
    document.addEventListener('submit', function(e) {
        // ─────────────────────────────────────────────────────────────────────
        // Language selector form submission
        // ─────────────────────────────────────────────────────────────────────
        const languageForm = e.target.closest(SELECTORS.languageForm);
        if (languageForm) {
            if (languageForm.dataset.submitting === 'true') {
                e.preventDefault();
                return;
            }

            const fallbackSubmitter = document.activeElement && document.activeElement.form === languageForm
                ? document.activeElement
                : null;
            const submitter = e.submitter || fallbackSubmitter;
            const submitterName = submitter ? submitter.getAttribute('name') : null;
            const submitterValue = submitter ? submitter.value : null;

            // If no reliable submitter is available, keep default browser submit behavior.
            if (!submitterName || !submitterValue) {
                return;
            }

            e.preventDefault();
            languageForm.dataset.submitting = 'true';

            const menu = languageForm.closest('.header-icons__language-menu');
            const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            const submitDelayMs = reduceMotion ? 0 : 220;
            let hiddenSubmitInput = languageForm.querySelector('input[data-language-submit-hidden]');

            if (!hiddenSubmitInput) {
                hiddenSubmitInput = document.createElement('input');
                hiddenSubmitInput.type = 'hidden';
                hiddenSubmitInput.dataset.languageSubmitHidden = 'true';
                languageForm.appendChild(hiddenSubmitInput);
            }

            hiddenSubmitInput.name = submitterName;
            hiddenSubmitInput.value = submitterValue;

            if (submitter) {
                submitter.disabled = true;
                submitter.setAttribute('aria-disabled', 'true');
            }

            if (menu) {
                menu.classList.add('is-leaving');
            }

            window.setTimeout(() => {
                languageForm.submit();
            }, submitDelayMs);

            return;
        }

        // ─────────────────────────────────────────────────────────────────────
        // Collection search form submission
        // ─────────────────────────────────────────────────────────────────────
        const collectionSearch = e.target.closest(SELECTORS.collectionSearch);
        if (collectionSearch) {
            e.preventDefault();
            const input = collectionSearch.querySelector('input[name="q"]');
            const term = input ? input.value : '';
            if (term.trim()) {
                const searchAction = collectionSearch.getAttribute('action');
                loadSearchContent(term.trim(), searchAction); 
            }
        }
    });

    // =========================================================================
    // Dropdown-wrapper hover handlers
    // =========================================================================

    document.addEventListener('DOMContentLoaded', function () {
        // Dropdown hover/click logic via delegation or individual observers
        // Note: Using individual observers here because dropdowns are static and need hover listeners
        const initDropdown = (dropdownWrapper) => {
            const trigger = dropdownWrapper.querySelector(SELECTORS.dropdownTrigger);
            let isTouch = false;

            dropdownWrapper.addEventListener('touchstart', () => { isTouch = true; }, { passive: true });

            dropdownWrapper.addEventListener('mouseenter', () => {
                if (isTouch) return;
                dropdownWrapper.classList.add('shown');
                if (trigger) trigger.setAttribute('aria-expanded', 'true');
            });

            dropdownWrapper.addEventListener('mouseleave', () => {
                if (isTouch) return;
                dropdownWrapper.classList.remove('shown');
                if (trigger) trigger.setAttribute('aria-expanded', 'false');
            });

            dropdownWrapper.addEventListener('click', (e) => {
                const isTrigger = e.target.closest(SELECTORS.dropdownTrigger);
                if (isTrigger) {
                    // On touch devices, we strictly use click/tap to toggle
                    const isShown = dropdownWrapper.classList.contains('shown');
                    
                    // Close all other dropdowns
                    document.querySelectorAll(SELECTORS.dropdownWrapper).forEach(other => {
                        if (other !== dropdownWrapper) {
                            other.classList.remove('shown');
                            const otherTrigger = other.querySelector(SELECTORS.dropdownTrigger);
                            if (otherTrigger) otherTrigger.setAttribute('aria-expanded', 'false');
                        }
                    });

                    dropdownWrapper.classList.toggle('shown', !isShown);
                    isTrigger.setAttribute('aria-expanded', (!isShown).toString());
                    
                    e.stopPropagation();
                    e.preventDefault();
                }

                if (e.target.closest(SELECTORS.dropdownTab)) {  
                    dropdownWrapper.classList.remove('shown');
                    if (trigger) trigger.setAttribute('aria-expanded', 'false');
                }
            });
        };

        document.querySelectorAll(SELECTORS.dropdownWrapper).forEach(initDropdown);

        // Close dropdowns when clicking outside
        document.addEventListener('click', (e) => {
            if (!e.target.closest(SELECTORS.dropdownWrapper)) {
                document.querySelectorAll(SELECTORS.dropdownWrapper).forEach(wrapper => {
                    const wasShown = wrapper.classList.contains('shown');
                    if (wasShown) {
                        wrapper.classList.remove('shown');
                        const trigger = wrapper.querySelector(SELECTORS.dropdownTrigger);
                        if (trigger) trigger.setAttribute('aria-expanded', 'false');
                    }
                });
            }
        });
    });

    // =========================================================================
    // BROWSER HISTORY (Back/Forward buttons)
    // =========================================================================
    window.addEventListener('popstate', function(event) {
        if (window.innerWidth <= 768) return;

        // Handle collection hash navigation
        const hash = window.location.hash.slice(1);
        if (hash) {
            const container = document.querySelector(SELECTORS.collectionContainer);
            if (container) {
                const targetItem = container.querySelector(`${SELECTORS.collectionMenuH2}[data-handle="${hash}"]`);
                if (targetItem) {
                    // Update active states
                    container.querySelectorAll(SELECTORS.collectionMenuH2).forEach(item => {
                        item.classList.toggle('active', item === targetItem);
                    });
                    
                    // Load collection content
                    const collectionUrl = targetItem.dataset.href;
                    loadCollectionContent(hash, collectionUrl, false, CONTENT_FADE_DURATION);
                    return;
                }
            }
        }

        // If not handled by hash navigation, reload to ensure correctly initialized state.
        // This is crucial when navigating between different templates (e.g. from product to collection).
        window.location.reload();
    });

    // Also listen for hashchange (covers edge cases)
    window.addEventListener('hashchange', function() {
        if (window.innerWidth <= 768) return;
        
        const hash = window.location.hash.slice(1);
        if (!hash) return;
        
        const container = document.querySelector(SELECTORS.collectionContainer);
        if (!container) return;
        
        const targetItem = container.querySelector(`${SELECTORS.collectionMenuH2}[data-handle="${hash}"]`);
        if (targetItem && !targetItem.classList.contains('active')) {
            // Update active states
            container.querySelectorAll(SELECTORS.collectionMenuH2).forEach(item => {
                item.classList.toggle('active', item === targetItem);
            });
            
            // Load collection content
            const collectionUrl = targetItem.dataset.href;
            loadCollectionContent(hash, collectionUrl, false, CONTENT_FADE_DURATION);
        }
    });

    // =========================================================================
    // LAYOUT & STYLING FUNCTIONS
    // =========================================================================

    /**
     * Positions the logo container as the middle child of header-desktop__content
     * Calculates the position based on the number of <h2> menu items
     */
    function centerLogoInHeader() {
        const headerContent = document.querySelector(SELECTORS.headerContent);
        const logoContainer = document.querySelector(SELECTORS.logoContainer);
        
        if (!headerContent || !logoContainer) return;

        // Count h2 children (menu items only, not including logo)
        const h2Elements = Array.from(headerContent.children).filter(el => el.tagName === 'H2');
        const count = h2Elements.length;

        if (count === 0) return;

        // Calculate target position: ceil((count + 1) / 2)
        // This ensures logo splits the menu items evenly
        const targetPosition = Math.ceil((count + 1) / 2);

        // Get the h2 at the target position (0-indexed, so subtract 1)
        const insertBeforeElement = h2Elements[targetPosition - 1];

        // Move logo to that position
        if (insertBeforeElement) {
            headerContent.insertBefore(logoContainer, insertBeforeElement);
        }
    }

    // =========================================================================
    // HOMEPAGE BANNER INITIALIZATION
    // =========================================================================

    function clamp(value, min, max) {
        return Math.min(Math.max(value, min), max);
    }

    function addMediaQueryChangeListener(mediaQuery, listener) {
        if (typeof mediaQuery.addEventListener === 'function') {
            mediaQuery.addEventListener('change', listener);
            return;
        }

        if (typeof mediaQuery.addListener === 'function') {
            mediaQuery.addListener(listener);
        }
    }

    function createHomepageBannerMobileScrollState() {
        const mediaQueryMobile = window.matchMedia('(max-width: 768px)');
        const mediaQueryHover = window.matchMedia('(hover: hover)');
        const mediaQueryReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        const state = {
            banners: new Set(),
            bannerMeta: new WeakMap(),
            rafId: null,
            scheduleUpdate: null,
            unsubscribeScrollTask: null,
        };

        function getScrollTop() {
            return window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
        }

        function clearBannerAnimation(banner) {
            banner.style.removeProperty('--banner-mobile-scroll-color-progress');
            banner.style.removeProperty('--banner-mobile-scroll-zoom-progress');
        }

        function isMobileScrollAnimationEnabled() {
            return (
                mediaQueryMobile.matches
                && !mediaQueryHover.matches
                && !mediaQueryReducedMotion.matches
            );
        }

        function updateBannerAnimation() {
            state.rafId = null;

            state.banners.forEach((banner) => {
                if (!document.body.contains(banner)) {
                    state.banners.delete(banner);
                }
            });

            if (!state.banners.size) {
                return;
            }

            if (!isMobileScrollAnimationEnabled()) {
                state.banners.forEach(clearBannerAnimation);
                return;
            }

            const scrollTop = getScrollTop();
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
            const scrollRange = Math.max(viewportHeight * 0.9, 1);
            const activationOffset = viewportHeight * 0.12;

            state.banners.forEach((banner) => {
                const bannerRect = banner.getBoundingClientRect();
                const meta = state.bannerMeta.get(banner) || {};

                if (typeof meta.startScrollTop !== 'number') {
                    const bannerTopInDocument = bannerRect.top + scrollTop;
                    meta.startScrollTop = Math.max(bannerTopInDocument - activationOffset, 0);
                    state.bannerMeta.set(banner, meta);
                }

                const baseProgress = clamp((scrollTop - meta.startScrollTop) / scrollRange, 0, 1);
                const colorProgress = clamp(baseProgress * 1.4, 0, 1);
                const zoomProgress = clamp(baseProgress * 0.6, 0, 1);

                banner.style.setProperty('--banner-mobile-scroll-color-progress', colorProgress.toFixed(4));
                banner.style.setProperty('--banner-mobile-scroll-zoom-progress', zoomProgress.toFixed(4));
            });
        }

        state.scheduleUpdate = function scheduleUpdate() {
            if (state.rafId !== null) {
                return;
            }

            state.rafId = window.requestAnimationFrame(updateBannerAnimation);
        };

        state.unsubscribeScrollTask = subscribeScrollTask(() => {
            state.scheduleUpdate();
            return 0;
        }, { phase: 'high', priority: 30 });
        window.addEventListener('resize', state.scheduleUpdate);
        addMediaQueryChangeListener(mediaQueryMobile, state.scheduleUpdate);
        addMediaQueryChangeListener(mediaQueryHover, state.scheduleUpdate);
        addMediaQueryChangeListener(mediaQueryReducedMotion, state.scheduleUpdate);

        return state;
    }

    function initHomepageBannerMobileScroll(root = document) {
        const scope = root && typeof root.querySelectorAll === 'function' ? root : document;
        const banners = scope.querySelectorAll(SELECTORS.homepageBanner);

        if (!banners.length && !homepageBannerMobileScrollState) {
            return;
        }

        if (!homepageBannerMobileScrollState) {
            homepageBannerMobileScrollState = createHomepageBannerMobileScrollState();
        }

        banners.forEach((banner) => {
            homepageBannerMobileScrollState.banners.add(banner);
        });

        homepageBannerMobileScrollState.scheduleUpdate();
    }

    function initSectionSlidesIfPresent(root = document) {
        const section = root.querySelector(SELECTORS.slidesSection);
        if (!section) return;

        initSectionSlides(section);
    }


    // =========================================================================
    // INITIALIZATION
    // =========================================================================

    /**
     * Run on initial page load
     */
    function initOnLoad() {
        // centerLogoInHeader();
        initCookieBanner(document);
        initCookieSettingsTrigger(document);
        initHeaderMetrics(document);
        initFreeShippingBanner(document);
        syncPolicyThresholdPlaceholders(document);
        initCollectionFromHash(CONTENT_FADE_DURATION);
        initSearchInput();
        initHomepageBannerMobileScroll(document);
        initSectionSlidesIfPresent(document);
        initProjectGallery(document);
        initPoliciesNavigation(document);
    }

    /**
     * Run after AJAX navigation completes
     */
    function initAfterNavigation() {
        // centerLogoInHeader();
        initCookieBanner(document);
        initCookieSettingsTrigger(document);
        initHeaderMetrics(document);
        initFreeShippingBanner(document);
        syncPolicyThresholdPlaceholders(document);
        initCollectionFromHash(CONTENT_FADE_DURATION);
        initSearchInput();
        initHomepageBannerMobileScroll(document);
        initSectionSlidesIfPresent(document);
        initProjectGallery(document);
        initPoliciesNavigation(document);
    }

    // Initial load
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initOnLoad);
    } else {
        initOnLoad();
    }

    // Listen for navigation complete event from header.js
    window.addEventListener('theme:navigation:complete', initAfterNavigation);

    // Window resize (debounced)
    let resizeTimeout;
    window.addEventListener('resize', function() {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
            // resizeTabs();
        }, 50);
    });


    // Theme editor support (single listener)
    document.addEventListener('shopify:section:load', function(event) {
        // resizeTabs();

        // for banner section
        const section = document.getElementById(
            `shopify-section-${event.detail.sectionId}`
        );
        if (!section) return;
        initFreeShippingBanner(section);
        syncPolicyThresholdPlaceholders(section);
        initHomepageBannerMobileScroll(section);
        initSectionSlidesIfPresent(section);
        initProjectGallery(section);
        initPoliciesNavigation(section);
    });

})();
